import React, { useState, useEffect, useRef, useCallback, useContext } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { motion } from 'framer-motion'; // Import motion

import Spinner from './Spinner';
import * as api from '../services/api'; // Import all API functions
import { ResultContext } from '../contexts/ResultContext';

// Helper function to generate unique model ID for this session
const generateModelId = (baseName = "voca") => {
  const now = new Date();
  const timestamp = `${now.getFullYear().toString().slice(-2)}${(now.getMonth() + 1).toString().padStart(2, '0')}${now.getDate().toString().padStart(2, '0')}`;
  // Use a more unique identifier for production use
  return `${baseName}_${timestamp}`;
};

// Helper function to generate user ID string
const generateUserId = (name = "") => {
  return name.toLowerCase().replace(/\s+/g, '');
};

// Component to display results safely
export const ResultDisplay = ({ title, data }) => {
  if (!data) return null;

  let content;
  if (typeof data === 'string' && data.startsWith('<')) {
    // If it looks like HTML, render it dangerously
    content = <div className="prose prose-sm max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: data }} />;
  } else if (typeof data === 'object') {
    // If it's an object, stringify it (nicely formatted)
    content = <pre className="text-xs bg-gray-100 dark:bg-gray-700 p-2 rounded overflow-x-auto">{JSON.stringify(data, null, 2)}</pre>;
  } else {
    // Otherwise, display as is
    content = <div className="text-sm text-gray-700 dark:text-gray-300" dangerouslySetInnerHTML={{ __html: data }} />;
  }

  return (
    <div className="mt-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50">
      <h4 className="font-semibold text-md mb-2 text-gray-800 dark:text-white">{title}</h4>
      {content}
    </div>
  );
};

// New Component for displaying metrics nicely
const MetricDisplay = ({ label, value, isPercentage = false }) => {
  let displayValue = value;

  if (typeof value === 'number') {
    if (isPercentage) {
      displayValue = `${(value * 100).toFixed(1)}%`;
    } else if (Number.isInteger(value)) {
      // If it's an integer, display as is
      displayValue = value;
    } else {
      // If it's a float, show 2 decimal places
      displayValue = value.toFixed(2);
    }
  } else if (value === null || value === undefined) {
    displayValue = 'N/A'; // Handle null/undefined
  }
  // Keep strings as they are (e.g., 'N/A')

  return (
    <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      <span className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">{label}</span>
      <span className="text-lg font-semibold text-gray-900 dark:text-white">
        {displayValue}
      </span>
    </div>
  );
};

function VOCAInteraction({
  model,
  extraData,
  onTrainingComplete,
  onUploadComplete,
  onPredictionComplete // <-- Add new prop
}) {
  const { user } = useAuth0();
  const [currentStep, setCurrentStep] = useState('health_check');
  const [healthStatus, setHealthStatus] = useState(null); // initial, uploading, validating, training, polling_train, metrics, uploading_test, validating_test, predicting, polling_predict, results
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusMessage, setStatusMessage] = useState('');
  const [progress, setProgress] = useState(0); // For polling progress
  const [uploadProgress, setUploadProgress] = useState(0); // For upload progress

  // Training Flow State
  const [trainFile, setTrainFile] = useState(null);
  const [trainUploadResult, setTrainUploadResult] = useState(null);
  const [trainValidationResult, setTrainValidationResult] = useState(null);
  const [trainTask, setTrainTask] = useState(null);
  // State to hold the final raw result from the status endpoint upon completion
  const [finalTaskResult, setFinalTaskResult] = useState(null);

  // Testing/Prediction Flow State
  const [testFile, setTestFile] = useState(null);
  const [testUploadResult, setTestUploadResult] = useState(null);
  const [testValidationResult, setTestValidationResult] = useState(null);
  const [predictTask, setPredictTask] = useState(null); // Assuming predict might also be async
  const [predictResult, setPredictResult] = useState(null); // Keep this for predict-specific results? Or merge? For now, keep separate.

  // VOCA Analytics State
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsProgress, setAnalyticsProgress] = useState({});
  const [analyticsResults, setAnalyticsResults] = useState({
    topicInsights: null,
    operationalDashboard: null,
    executiveDashboard: null,
    recommendations: null,
    enhancedResults: null
  });
  const [analyticsTasks, setAnalyticsTasks] = useState({
    topicInsights: null,
    operationalDashboard: null,
    executiveDashboard: null,
    recommendations: null
  });

  // Generate IDs once per component instance
  const modelIdRef = useRef(generateModelId(model?.name?.toLowerCase().replace(/\s+/g, '_')));
  const userIdRef = useRef(generateUserId(user?.name || ''));

  // Polling ref
  const pollingIntervalRef = useRef(null);

  // Clear polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  const handleSampleFileDownload = (fileUrl) => {

    //const fileName = 'downloaded-file.pdf';

    try {

      setIsLoading(true)

      const link = document.createElement('a');
      link.href = fileUrl;
      // link.setAttribute('download', fileName); // optional, suggest filename
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setTimeout(() => {
        setIsLoading(false)
      }, 3000);

    } catch (error) {
      console.log('errr', error);

    }

  };

  // Common button class for primary actions
  const primaryButtonClass = "px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#0062cc] hover:bg-[#0052ac] disabled:opacity-50 disabled:cursor-not-allowed";
  // Common button class for secondary actions
  const secondaryButtonClass = "px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed";


  // --- Health Check Handler ---
  const handleHealthCheck = async () => {
    try {
      setIsLoading(true);
      setStatusMessage('Checking VOCA service health...');

      const response = await api.checkVOCAHealth();
      setHealthStatus(response);

      if (response?.overall_status === 'healthy' || response?.services?.api?.status === 'healthy') {
        setStatusMessage('VOCA service is healthy. Ready to proceed.');
        setCurrentStep('initial');
      } else {
        setError('VOCA service is not fully healthy. Some features may be limited.');
        setCurrentStep('initial'); // Allow to proceed anyway
      }
    } catch (error) {
      console.error('Health check failed:', error);
      setError(`Health check failed: ${error.message}. Proceeding anyway...`);
      setCurrentStep('initial'); // Allow to proceed anyway
    } finally {
      setIsLoading(false);
    }
  };

  // --- API Call Handlers ---

  const handleApiCall = async (apiFunc, params, successStep, resultSetter, progressCallback) => {
    setIsLoading(true);
    setError(null);
    setStatusMessage('Processing...');
    setProgress(0); // Reset polling progress
    setUploadProgress(0); // Reset upload progress
    try {
      // Pass progress callback if provided (only for upload)
      const result = await apiFunc(params, progressCallback);
      if (resultSetter) resultSetter(result);
      setCurrentStep(successStep);
      setUploadProgress(100); // Mark upload as complete on success
      return result;
    } catch (err) {
      console.error(`Error during ${successStep}:`, err);
      const errorMessage = err.response?.data?.detail || err.message || 'Unknown error';
      setError(`Operation failed: ${errorMessage}`);
      setStatusMessage(`Operation failed.`);
      setCurrentStep('error');
      return null;
    } finally {
      setIsLoading(false);
      // Clear status message only if no error occurred
      if (!error) {
        setStatusMessage('');
      }
    }
  };

  const handleUpload = async (dataType) => {
    const file = dataType === 'training' ? trainFile : testFile;
    if (!file || !user?.email) {
      setError(`Please select a ${dataType} file and ensure you are logged in.`);
      return;
    }

    setIsLoading(true);
    setError(null);
    setUploadProgress(0);
    setStatusMessage(`Uploading ${dataType} data...`);

    // Create FormData exactly as shown in the swagger UI example
    const formData = new FormData();
    formData.append('file', file); // Use the original CSV file
    formData.append('email_id', user.email);
    formData.append('user_id', user.email);
    formData.append('model_id', modelIdRef.current);
    formData.append('data_type', dataType);
    //formData.append('record_limit', '500'); // Demo limit

    const resultSetter = dataType === 'training' ? setTrainUploadResult : setTestUploadResult;
    const nextStep = dataType === 'training' ? 'training' : 'validating_test';

    // Define the progress callback for Axios
    const onUploadProgress = (progressEvent) => {
      if (progressEvent.total) {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
      } else {
        // Handle indeterminate progress if total size is not available
        setUploadProgress(50); // Example: set to 50% or use a different visual indicator
      }
    };

    // Call the generic handler, passing the progress callback
    const uploadResult = await handleApiCall(api.uploadVOCAData, formData, nextStep, resultSetter, onUploadProgress);

    // If upload was successful and callback exists, call it
    if (uploadResult && onUploadComplete) {
      onUploadComplete(uploadResult);
    }
  };

  const { setResult } = useContext(ResultContext);

  const handleValidate = async (dataType) => {
    const uploadResult = dataType === 'training' ? trainUploadResult : testUploadResult;
    if (!uploadResult?.file_details?.s3_key || !user?.email) {
      setError(`Cannot validate: Missing upload details or user email.`);
      return;
    }



    const params = {
      file_key: uploadResult.file_details.s3_key,
      email_id: user.email,
      user_id: user.email,
      model_id: modelIdRef.current,
      //data_type: dataType,
      // manual_mapping: '' // Add if manual mapping UI is implemented
    };

    setStatusMessage(`Validating ${dataType} data...`);

    const resultSetter = dataType === 'training' ? setTrainValidationResult : setTestValidationResult;
    const nextStep = dataType === 'training' ? 'training' : 'predicting';

    handleApiCall(api.validateVOCAProcess, params, nextStep, resultSetter);
  };

  const handleSampleUpload = async (dataType) => {
    setIsLoading(true);
    setError(null);
    setStatusMessage('Processing...');

    const uploadResult = dataType === 'training' ? trainUploadResult : testUploadResult;
    if (!user?.email) {
      setError(`Cannot validate: Missing upload details or user email.`);
      return;
    }

    const path = extraData?.training_url.split("/").slice(3).join("/").split("?")[0];

    console.log(path); // Output: sample-files/OnlineRetail_train.csv

    const params = {
      sample_s3_key: path,
      email_id: user.email,
      user_id: user.email,
      model_id: modelIdRef.current,
      data_type: dataType,
    };

    const nextStep = 'validating';

    const statusResult = await api.useSampleDataApi('vocaapi', params);
    setTrainUploadResult(statusResult)
    setCurrentStep(nextStep);

    if (statusResult) {
      onUploadComplete(statusResult);
      setStatusMessage(`Ready for next`);
      setIsLoading(false)
    }
  };

  const handleTrain = async () => {
    if (!trainUploadResult?.file_details?.s3_key || !user?.email) {
      setError(`Cannot process: Missing upload details or user email.`);
      return;
    }

    try {
      setStatusMessage('Starting VOCA analysis...');
      setIsLoading(true);

      const params = {
        file_key: trainUploadResult.file_details.s3_key,
        email_id: user.email,
        model_id: modelIdRef.current,
        user_id: generateUserId(user.name || user.email)
      };

      const response = await api.validateVOCAProcess(params);

      if (response?.task_id) {
        setTrainTask(response);
        setCurrentStep('polling_train');
        setStatusMessage('Starting VOCA analysis...');
        startPolling(response.task_id, 'results');
      } else {
        throw new Error('No task ID received from process endpoint');
      }
    } catch (error) {
      console.error('Error processing VOCA data:', error);
      setError(`Failed to process data: ${error.message}`);
      setIsLoading(false);
    }
  };

  const startPolling = (taskId, successStep) => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    setStatusMessage(`Task started. Monitoring progress...`);
    setProgress(0);
    setFinalTaskResult(null); // Ensure final result is clear at start of polling

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const statusResult = await api.getVOCATaskStatus(taskId);

        if (!statusResult || !statusResult.status) {
          console.warn("Polling received incomplete status result:", statusResult);
          // Optional: Keep polling or show an intermediate error?
          // setStatusMessage("Waiting for task status...");
          return; // Skip update if data is missing
        }

        const metadata = statusResult.metadata;

        const currentStage = metadata.stage;
        const currentApiStatus = statusResult.status;

        if (currentStage === 'completed' || currentApiStatus === 'completed') {
          // --- Task Completed ---
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setFinalTaskResult(statusResult); // Store the full final result
          setProgress(100); // Set progress to 100%
          setStatusMessage('Training Task Completed Successfully! Starting analytics...');

          // Start analytics automatically when training is completed
          if (statusResult.task_id) {
            startAllAnalytics(statusResult.task_id);
          } else {
            // Fallback to original step if no task_id available
            setCurrentStep(successStep);
            if (onTrainingComplete) {
              onTrainingComplete(statusResult);
            }
          }

        } else if (currentApiStatus === 'failed') {
          // --- Task Failed ---
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          const pollError = metadata?.error || statusResult.status || 'Unknown reason'; // Get error details
          setError(`Task failed: ${pollError}`);
          setStatusMessage('Task failed.');
          setCurrentStep('error');

        } else {
          // --- Task Still Processing ---
          const numericalProgress = metadata.progress || 0;
          const displayProgress = Math.min(numericalProgress, 75); // Cap progress at 75%
          setProgress(numericalProgress);
          // Use stage if available, otherwise fallback status
          const displayStage = currentStage || currentApiStatus || 'Processing';
          setStatusMessage(`Task Status: ${displayStage} (${numericalProgress}%)`);
        }
      } catch (err) {
        console.error('Polling error:', err);
        setError(`Failed to get task status: ${err.message || 'Polling request failed'}`);
        // Decide whether to stop polling on error
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
        setCurrentStep('error'); // Go to error state on polling failure
      }
    }, 5000); // Poll every 5 seconds
  };

  const handleGetResults = async (taskId) => {
    try {
      setStatusMessage('Fetching analysis results...');
      setIsLoading(true);

      // Get results from the VOCA API
      const response = await api.getVOCATaskStatus(taskId);

      if (response && response.status === 'completed') {
        // Call the results endpoint to get detailed analysis using apiClient
        const resultsData = await api.predictVOCAModel({ task_id: taskId });
        setResult(resultsData);

        // If callback exists, call it with the results
        if (onPredictionComplete) {
          onPredictionComplete(resultsData);
        }

        setCurrentStep('results');
        setStatusMessage('Analysis completed successfully.');
      } else {
        throw new Error('Task not completed yet');
      }
    } catch (error) {
      console.error('Error fetching VOCA results:', error);
      setError(`Failed to get results: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ==================== VOCA ANALYTICS FUNCTIONS ====================

  // Start analytics one by one sequentially after training is completed
  const startAllAnalytics = async (taskId) => {
    try {
      setAnalyticsLoading(true);
      setStatusMessage('Starting analytics generation...');
      setCurrentStep('analytics_starting');

      // Get enhanced results immediately (this should be available)
      const enhancedResults = await api.getVOCAResults(taskId, true);
      setAnalyticsResults(prev => ({
        ...prev,
        enhancedResults
      }));

      // Start analytics sequentially
      await startSequentialAnalytics(taskId);

    } catch (error) {
      console.error('Error starting analytics:', error);
      setError(`Failed to start analytics: ${error.message}`);
      setCurrentStep('error');
    }
  };

  // Transform analytics result based on analytics_type to expected format
  const transformAnalyticsResult = (result, expectedType) => {
    if (!result || !result.analytics_type) {
      return result;
    }

    // Map analytics_type to expected component key names
    const typeMapping = {
      'topic_insights': 'topicInsights',
      'operational_dashboard': 'operationalDashboard',
      'executive_dashboard': 'executiveDashboard',
      'recommendations': 'recommendations'
    };

    const mappedType = typeMapping[result.analytics_type] || expectedType;

    console.log(`Transforming ${result.analytics_type} -> ${mappedType}`, result);

    // Return the result in the expected format
    return result;
  };

  // Start and complete analytics tasks one by one sequentially
  const startSequentialAnalytics = async (taskId) => {
    const analyticsTypes = [
      { type: 'topicInsights', label: 'Topic Insights', apiCall: () => api.startVOCATopicInsights(taskId, 5, 'all') },
      { type: 'operationalDashboard', label: 'Operational Dashboard', apiCall: () => api.startVOCAOperationalDashboard(taskId) },
      { type: 'executiveDashboard', label: 'Executive Dashboard', apiCall: () => api.startVOCAExecutiveDashboard(taskId) },
      { type: 'recommendations', label: 'Recommendations', apiCall: () => api.startVOCARecommendations(taskId, 'all', 'all', false, true) }
    ];

    const completedResults = {};

    for (const { type, label, apiCall } of analyticsTypes) {
      try {
        setStatusMessage(`Generating ${label}...`);

        // Start the analytics task
        const taskResponse = await apiCall();
        const analyticsTaskId = taskResponse?.task_id;

        if (!analyticsTaskId) {
          console.warn(`No task ID received for ${type}`);
          continue;
        }

        // Update tasks state
        setAnalyticsTasks(prev => ({
          ...prev,
          [type]: analyticsTaskId
        }));

        // Poll this specific task until completion
        const result = await pollSingleAnalyticsTask(analyticsTaskId, type, label);

        if (result) {
          // Transform the result to match expected format
          const transformedResult = transformAnalyticsResult(result, type);
          completedResults[type] = transformedResult;
          setAnalyticsResults(prev => ({
            ...prev,
            [type]: transformedResult
          }));
        }

      } catch (error) {
        console.error(`Error with ${type}:`, error);
        // Continue with other analytics even if one fails
      }
    }

    // All analytics completed
    setAnalyticsLoading(false);
    setStatusMessage('All analytics completed successfully!');
    setCurrentStep('analytics_completed');

    // Call the callback with all analytics results
    if (onTrainingComplete) {
      onTrainingComplete({
        ...finalTaskResult,
        analytics: { ...analyticsResults, ...completedResults }
      });
    }
  };

  // Poll a single analytics task until completion
  const pollSingleAnalyticsTask = async (analyticsTaskId, type, label) => {
    return new Promise((resolve) => {
      const pollInterval = setInterval(async () => {
        try {
          const status = await api.getVOCAAnalyticsTaskStatus(analyticsTaskId);

          if (status?.status === 'completed') {
            clearInterval(pollInterval);
            setStatusMessage(`${label} completed successfully`);
            resolve(status.results);
          } else if (status?.status === 'in_progress') {
            // Update progress for this specific task
            setAnalyticsProgress(prev => ({
              ...prev,
              [type]: {
                progress: status.progress || 0,
                stage: status.stage || 'processing'
              }
            }));
            setStatusMessage(`${label}: ${status.stage || 'processing'} (${status.progress || 0}%)`);
          } else if (status?.status === 'failed') {
            clearInterval(pollInterval);
            console.error(`${type} analytics failed`);
            resolve(null);
          }
        } catch (error) {
          console.error(`Error polling ${type}:`, error);
          // Continue polling on individual request errors
        }
      }, 3000); // Poll every 3 seconds

      // Timeout after 10 minutes
      setTimeout(() => {
        clearInterval(pollInterval);
        console.warn(`${type} analytics timed out`);
        resolve(null);
      }, 600000);
    });
  };

  // const sentimentData = {
  //   comment_count: 92,
  //   sentiment_counts: {
  //     positive: 53,
  //     negative: 30,
  //     neutral: 9,
  //     unknown: 0,
  //   },
  //   net_sentiment_score: 3.12,
  //   average_feedback_length: 202.6,
  //   top_emotions: ['happy', 'satisfied', 'frustrated', 'disappointed', 'angry'],
  //   top_topics: ['camera', 'display', 'battery', 'price', 'performance'],
  // };

  const SentimentCard = (sentimentData2) => {


    const { comment_count, sentiment_counts, net_sentiment_score, average_feedback_length, top_emotions, top_topics } =
      sentimentData2.metadata;

    return (
      <div className="max-w-4xl mx-auto p-6 bg-gray-50 min-h-screen">
        <div className="bg-white shadow-lg rounded-2xl p-6 space-y-6">
          <h2 className="text-2xl font-semibold text-gray-800">📊 Customer Sentiment Analysis</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-xl text-center">
              <div className="text-sm text-gray-500">Total Comments</div>
              <div className="text-xl font-bold text-blue-600">{comment_count}</div>
            </div>
            <div className="bg-green-50 p-4 rounded-xl text-center">
              <div className="text-sm text-gray-500">Net Sentiment</div>
              <div className="text-xl font-bold text-green-600">{net_sentiment_score}</div>
            </div>
            <div className="bg-yellow-50 p-4 rounded-xl text-center">
              <div className="text-sm text-gray-500">Avg. Feedback Length</div>
              <div className="text-xl font-bold text-yellow-600">{average_feedback_length}</div>
            </div>
            <div className="bg-gray-100 p-4 rounded-xl text-center">
              <div className="text-sm text-gray-500">Unknown Sentiment</div>
              <div className="text-xl font-bold text-gray-600">{sentiment_counts?.unknown}</div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Sentiment Breakdown</h3>
            <div className="flex gap-4">
              {Object.entries(sentiment_counts).map(([type, count]) => {
                const colorMap = {
                  positive: 'text-green-600 bg-green-100',
                  negative: 'text-red-600 bg-red-100',
                  neutral: 'text-yellow-600 bg-yellow-100',
                  unknown: 'text-gray-600 bg-gray-100',
                };
                return (
                  <span
                    key={type}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${colorMap[type] || 'bg-gray-200 text-gray-700'}`}
                  >
                    {type.charAt(0).toUpperCase() + type.slice(1)}: {count}
                  </span>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">💬 Top Emotions</h3>
              <div className="flex flex-wrap gap-2">
                {top_emotions.map((emotion, idx) => (
                  <span key={idx} className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-sm">
                    {emotion}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-2">🔥 Top Topics</h3>
              <div className="flex flex-wrap gap-2">
                {top_topics.map((topic, idx) => (
                  <span key={idx} className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm">
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // --- Render Logic ---

  const renderStepUI = () => {
    // Helper function to wrap step content in consistent styling
    const stepContent = (content, title) => (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-800/50"
      >
        <h4 className="text-lg font-semibold text-gray-800 dark:text-white mb-3">{title}</h4>
        <div className="space-y-4">
          {content}
        </div>
      </motion.div>
    );

    switch (currentStep) {
      case 'health_check':
        return stepContent(
          <>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Checking VOCA service health before proceeding with analysis.
            </p>
            {healthStatus && (
              <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Service Status:</h5>
                <div className="space-y-1 text-xs">
                  {Object.entries(healthStatus.services || {}).map(([service, status]) => (
                    <div key={service} className="flex justify-between">
                      <span className="capitalize">{service}:</span>
                      <span className={`font-medium ${status.status === 'healthy' ? 'text-green-600' : 'text-yellow-600'
                        }`}>
                        {status.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <button
              onClick={handleHealthCheck}
              disabled={isLoading}
              className={primaryButtonClass}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Checking...</> : 'Check Service Health'}
            </button>
          </>,
          "Health Check"
        );
      case 'initial':
      case 'uploading': // Allow re-upload
        return stepContent(
          <>
            <button
              onClick={() => handleSampleUpload('training')}
              disabled={isLoading}
              className={primaryButtonClass}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Uploading...</> : 'Use Sample Data'}
            </button>
            <p>OR</p>
            <span
              class="underline text-blue-600 cursor-pointer hover:text-blue-800 text-sm"

              onClick={() => handleSampleFileDownload(extraData?.training_url)}
            >
              Download sample training file
            </span>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Select your training data file (.csv expected).</p>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setTrainFile(e.target.files[0])}
              className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-[#0062cc]/10 dark:file:bg-[#0062cc]/20 file:text-[#0062cc] dark:file:text-blue-300 hover:file:bg-[#0062cc]/20 dark:hover:file:bg-[#0062cc]/30 mb-2"
            />
            {/* Upload Progress Bar */}
            {isLoading && uploadProgress > 0 && (
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 my-1">
                <div className="bg-[#0062cc] h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
              </div>
            )}
            <button
              onClick={() => handleUpload('training')}
              disabled={!trainFile || isLoading}
              className={primaryButtonClass}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Uploading...</> : 'Upload Training Data'}
            </button>
          </>,
          "1. Upload Training Data"
        );
      case 'validating':
        const trainRows = trainUploadResult?.file_details?.total_rows || 'N/A';
        const trainCols = trainUploadResult?.file_details?.columns || 'N/A';
        return stepContent(
          <>
            {/* Show Rows and Columns instead of full JSON */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <MetricDisplay label="Rows Processed" value={trainRows} />
              <MetricDisplay label="Columns Found" value={trainCols} />
            </div>
            {/* Display original filename for context */}
            {trainUploadResult?.file_details?.original_filename && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                File: {trainUploadResult.file_details.original_filename}
              </p>
            )}
            <button
              onClick={() => handleValidate('training')}
              disabled={isLoading}
              className={primaryButtonClass}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Validating...</> : 'Validate Training Data'}
            </button>
          </>,
          "2. Validate Training Data"
        );
      case 'training':
        const trainValResult = trainValidationResult;
        console.debug("Train Validation Result", trainValResult);
        const trainValCols = trainValResult?.file_details?.columns?.length || 'N/A';
        const trainMapping = trainValResult?.details?.mapping;
        const trainMappingValues = trainMapping ? Object.values(trainMapping) : [];

        return stepContent(
          <>
            {/* Show specific validation results */}
            {/* <MetricDisplay label="Columns Validated" value={trainValCols} /> */}

            {trainMappingValues.length > 0 && (
              <div className="mt-4">
                <h5 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-2">Input Columns Used:</h5>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  {trainMappingValues.map((colName, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      {colName}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Display validation message */}
            {trainValResult?.message && (
              <p className="text-sm text-green-600 dark:text-green-400 mt-4 bg-green-50 dark:bg-green-900/30 p-2 rounded">
                {trainValResult.message}
              </p>
            )}

            <button
              onClick={handleTrain}
              disabled={isLoading}
              className={`${primaryButtonClass} mt-4`}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Starting...</> : 'Start Training'}
            </button>
          </>,
          "3. Train Model"
        );
      case 'polling_train':
        return stepContent(
          <>
            <h4 className="text-md font-semibold text-gray-800 dark:text-white">Training in Progress...</h4>
            {/* <p className="text-sm text-gray-600 dark:text-gray-400">Task ID: {trainTask?.task_id}</p> */}
            {/* Polling Progress Bar - capped at 75% visually during polling */}
            <div>
              <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 my-1">
                <div className="bg-[#0062cc] h-2.5 rounded-full transition-width duration-500 ease-out" style={{ width: `${progress}%` }}></div>
              </div>

              {/* Show 75% max until truly complete */}
              <p className="text-xs text-center text-gray-600 dark:text-gray-400">{progress === 100 ? '100%' : `${progress}%`} Complete</p>
            </div>
            {/* No button needed here now, automatically proceeds on completion */}
          </>,
          "Training Status" // Simplified title
        );
      // Removed 'metrics' step - now handled by 'results' step after polling
      case 'uploading_test':
        return stepContent(
          <>
            <h4 className="text-md font-semibold text-gray-800 dark:text-white">4. Upload Testing Data</h4>
            <span
              className="underline text-blue-600 cursor-pointer hover:text-blue-800 text-sm"

              onClick={() => handleSampleFileDownload(extraData?.testing_url)}
            >
              Download sample testing file
            </span>
            <p className="text-sm text-gray-600 dark:text-gray-300">Select your testing data file (.csv expected).</p>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => { setTestFile(e.target.files[0]); setUploadProgress(0); setError(null); }}
              className="block w-full text-sm text-gray-500 dark:text-gray-400 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-gray-300 dark:file:border-gray-600 file:text-sm file:font-semibold file:bg-gray-100 dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-200 dark:hover:file:bg-gray-600"
            />
            {/* Upload Progress Bar */}
            {isLoading && currentStep === 'uploading_test' && uploadProgress > 0 && (
              <div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 my-1">
                  <div className="bg-green-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <p className="text-xs text-center text-gray-600 dark:text-gray-400">Uploading: {uploadProgress}%</p>
              </div>
            )}
            <button
              onClick={() => handleUpload('testing')}
              disabled={!testFile || isLoading}
              className={primaryButtonClass}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Uploading...</> : 'Upload Testing Data'}
            </button>
          </>,
          "4. Upload Testing Data"
        );
      case 'validating_test':
        const testRows = testUploadResult?.file_details?.rows || 'N/A';
        const testCols = testUploadResult?.file_details?.columns || 'N/A';
        return stepContent(
          <>
            {/* Show Rows and Columns instead of full JSON */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              <MetricDisplay label="Rows Processed" value={testRows} />
              <MetricDisplay label="Columns Found" value={testCols} />
            </div>
            {/* Display original filename for context */}
            {testUploadResult?.file_details?.original_filename && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                File: {testUploadResult.file_details.original_filename}
              </p>
            )}
            <button
              onClick={() => handleValidate('testing')}
              disabled={isLoading}
              className={primaryButtonClass}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Validating...</> : 'Validate Testing Data'}
            </button>
          </>,
          "5. Validate Testing Data"
        );
      case 'predicting':
        const testValResult = testValidationResult;
        console.log("Test Validation Result", testValResult);
        const testValCols = testValResult?.details?.data_shape?.columns || 'N/A';
        const testMapping = testValResult?.details?.mapping;
        const testMappingValues = testMapping ? Object.values(testMapping) : [];

        return stepContent(
          <>
            {/* Show specific validation results */}
            {/* <MetricDisplay label="Columns Validated" value={testValCols} /> */}

            {testMappingValues.length > 0 && (
              <div className="mt-4">
                <h5 className="text-md font-semibold text-gray-700 dark:text-gray-300 mb-2">Input Columns Used:</h5>
                <ul className="list-disc list-inside space-y-1 pl-2">
                  {testMappingValues.map((colName, index) => (
                    <li key={index} className="text-sm text-gray-600 dark:text-gray-400">
                      {colName}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Display validation message */}
            {testValResult?.message && (
              <p className="text-sm text-green-600 dark:text-green-400 mt-4 bg-green-50 dark:bg-green-900/30 p-2 rounded">
                {testValResult.message}
              </p>
            )}

            <button
              onClick={() => handleGetResults(finalTaskResult?.task_id)}
              disabled={isLoading || !finalTaskResult?.task_id}
              className={`${primaryButtonClass} mt-4`}
            >
              {isLoading ? <><Spinner size="xs" className="mr-2" /> Getting Results...</> : 'Get Analysis Results'}
            </button>
          </>,
          "6. Predict"
        );
      case 'results': // Combined results step
        if (predictResult) {
          // --- Display Prediction Results --- 
          const stats = predictResult.segment_stats;
          const metrics = predictResult.metrics?.test_metrics;
          const liftMetrics = predictResult.lift_metrics;
          const preview = predictResult.preview_data;

          // --- Parse lift data --- 
          let parsedLiftData = [];
          if (liftMetrics?.lift_values) {
            try {
              const rawLiftData = JSON.parse(liftMetrics.lift_values);

              if (rawLiftData.index && rawLiftData['Lift (Total Sales)'] && rawLiftData['Cumulative Lift']) {
                parsedLiftData = rawLiftData.index.map((idx, i) => ({
                  index: idx,
                  'Lift (Total Sales)': rawLiftData['Lift (Total Sales)'][i] || 0,
                  'Cumulative Lift': rawLiftData['Cumulative Lift'][i] || 0,
                }));
              }
            } catch (parseError) {
              console.error("Error parsing lift_values JSON:", parseError);
            }
          }
          // --- End Parse lift data --- 

          return stepContent(
            <>
              {/* Summary Stats */}
              {stats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <MetricDisplay label="Total Customers" value={stats.total_customers || 'N/A'} />
                  <MetricDisplay label="High-Value Customers" value={stats.high_value_count || 'N/A'} />
                  <MetricDisplay label="Regular Customers" value={stats.regular_count || 'N/A'} />
                  <MetricDisplay label="High-Value Ratio" value={stats.high_value_ratio || 0} isPercentage={true} />
                </div>
              )}

              {/* Prediction/Test Metrics */}
              {metrics && (
                <div className="mb-6">
                  <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-3">Prediction Performance Metrics</h5>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <MetricDisplay label="Accuracy" value={metrics.accuracy || 0} isPercentage={true} />
                    <MetricDisplay label="Precision" value={metrics.precision || 0} isPercentage={false} />
                    <MetricDisplay label="Recall" value={metrics.recall || 0} isPercentage={false} />
                    <MetricDisplay label="F1 Score" value={metrics.f1 || 0} isPercentage={false} />
                  </div>
                </div>
              )}



              {/* Start Over Button */}
              <div className="mt-6">
                <button
                  onClick={() => {
                    setCurrentStep('initial');
                    setError(null);
                    setStatusMessage('');
                    setTrainFile(null);
                    setTestFile(null);
                    setTrainUploadResult(null);
                    setTestUploadResult(null);
                    setTrainValidationResult(null);
                    setTestValidationResult(null);
                    setTrainTask(null);
                    setPredictTask(null);
                    setFinalTaskResult(null);
                    setPredictResult(null); // Also clear predict result
                    setProgress(0);
                    setUploadProgress(0);
                  }}
                  className={secondaryButtonClass}
                >
                  Start Over
                </button>
              </div>
            </>,
            "Prediction Results"
          );
        } else if (finalTaskResult) {
          // --- Display Training Results (Simplified) ---
          const resultTitle = "";

          return stepContent(
            <>
              {/* Simple Confirmation Message */}
              <p className="text-lg text-center text-green-600 dark:text-green-400 font-semibold py-4">
                Training Task Completed Successfully!
              </p>


              {/* {SentimentCard(finalTaskResult)} */}



              {/* Action Buttons */}
              {/* <div className="mt-6 flex justify-center space-x-4">  
                <button
                  onClick={() => setCurrentStep('uploading_test')}
                  className={primaryButtonClass}
                >
                  Proceed to Testing
                </button>
                <button
                  onClick={() => {
                    setCurrentStep('initial');
                    setError(null);
                    setStatusMessage('');
                    setTrainFile(null);
                    setTestFile(null);
                    setTrainUploadResult(null);
                    setTestUploadResult(null);
                    setTrainValidationResult(null);
                    setTestValidationResult(null);
                    setTrainTask(null);
                    setPredictTask(null);
                    setFinalTaskResult(null);
                    setPredictResult(null);
                    setProgress(0);
                    setUploadProgress(0);
                  }}
                  className={secondaryButtonClass}
                >
                  Start Over
                </button>
              </div> */}

            </>,
            resultTitle
          );
        } else {
          // Should not happen if logic is correct, but provide a fallback
          return stepContent(<p>Waiting for results...</p>, "Results");
        }
      case 'analytics_starting':
        return stepContent(
          <>
            <div className="text-center">
              <h4 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                🚀 Starting Advanced Analytics
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Generating comprehensive insights including topic analysis, dashboards, and strategic recommendations...
              </p>
              <div className="flex justify-center">
                <Spinner size="md" />
              </div>
            </div>
          </>,
          "Analytics Generation"
        );

      case 'analytics_completed':
        return stepContent(
          <>
            <div className="text-center mb-6">
              <h4 className="text-lg font-semibold text-green-600 dark:text-green-400 mb-2">
                ✅ All Analytics Completed Successfully!
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Your comprehensive VOCA analysis is ready. View results in the Insights and Visualizations tabs.
              </p>
            </div>

            {/* Analytics Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <h5 className="text-sm font-semibold text-blue-700 dark:text-blue-300 mb-1">Topic Insights</h5>
                <p className="text-xs text-blue-600 dark:text-blue-400">
                  {analyticsResults.topicInsights ? '✓ Complete' : '⏳ Processing'}
                </p>
              </div>
              <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                <h5 className="text-sm font-semibold text-green-700 dark:text-green-300 mb-1">Operational Dashboard</h5>
                <p className="text-xs text-green-600 dark:text-green-400">
                  {analyticsResults.operationalDashboard ? '✓ Complete' : '⏳ Processing'}
                </p>
              </div>
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <h5 className="text-sm font-semibold text-purple-700 dark:text-purple-300 mb-1">Executive Dashboard</h5>
                <p className="text-xs text-purple-600 dark:text-purple-400">
                  {analyticsResults.executiveDashboard ? '✓ Complete' : '⏳ Processing'}
                </p>
              </div>
              <div className="p-4 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
                <h5 className="text-sm font-semibold text-orange-700 dark:text-orange-300 mb-1">Recommendations</h5>
                <p className="text-xs text-orange-600 dark:text-orange-400">
                  {analyticsResults.recommendations ? '✓ Complete' : '⏳ Processing'}
                </p>
              </div>
            </div>

            {/* Enhanced Results Preview */}
            {analyticsResults.enhancedResults?.analysis_summary && (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 mb-4">
                <h5 className="text-md font-semibold text-gray-800 dark:text-white mb-3">Quick Analysis Summary</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <MetricDisplay
                    label="Total Comments"
                    value={analyticsResults.enhancedResults.analysis_summary.total_comments}
                  />
                  <MetricDisplay
                    label="Success Rate"
                    value={analyticsResults.enhancedResults.analysis_summary.analysis_success_rate}
                    isPercentage={true}
                  />
                  <MetricDisplay
                    label="Positive Sentiment"
                    value={analyticsResults.enhancedResults.analysis_summary.sentiment_distribution?.positive || 0}
                  />
                  <MetricDisplay
                    label="Negative Sentiment"
                    value={analyticsResults.enhancedResults.analysis_summary.sentiment_distribution?.negative || 0}
                  />
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => {
                  // Reset for new analysis
                  setCurrentStep('initial');
                  setError(null);
                  setStatusMessage('');
                  setTrainFile(null);
                  setTrainUploadResult(null);
                  setTrainValidationResult(null);
                  setTrainTask(null);
                  setFinalTaskResult(null);
                  setAnalyticsResults({
                    topicInsights: null,
                    operationalDashboard: null,
                    executiveDashboard: null,
                    recommendations: null,
                    enhancedResults: null
                  });
                  setAnalyticsTasks({
                    topicInsights: null,
                    operationalDashboard: null,
                    executiveDashboard: null,
                    recommendations: null
                  });
                  setProgress(0);
                  setUploadProgress(0);
                  setAnalyticsLoading(false);
                  setAnalyticsProgress({});
                }}
                className={secondaryButtonClass}
              >
                Start New Analysis
              </button>
            </div>
          </>,
          "Analytics Complete"
        );

      case 'error':
        return stepContent(
          <>
            <p className="text-red-600 dark:text-red-400">An error occurred. Please check the message below and try again.</p>
            <button
              onClick={() => {
                setError(null);
                setStatusMessage('');
                // Reset relevant states to allow retry from initial step
                setCurrentStep('initial');
                setTrainFile(null);
                setTestFile(null);
                // ... maybe reset other states depending on where the error occurred
              }}
              className={primaryButtonClass}
            >
              Try Again
            </button>
          </>,
          "Operation Failed"
        );
      default:
        return <p>Unknown step: {currentStep}</p>;
    }
  };

  return (
    <div className="space-y-6 p-4 border rounded-lg bg-white dark:bg-gray-900 shadow-sm">
      {/* <h3 className="text-xl font-semibold border-b border-gray-200 dark:border-gray-700 pb-2 text-gray-900 dark:text-white">Lookalike Model Workflow</h3> */}

      {/* Status Area */}
      <div className="p-3 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 min-h-[60px] flex items-center">
        {isLoading && !statusMessage.toLowerCase().includes("uploading") && ( // Don't show generic spinner during upload progress
          <div className="flex items-center space-x-2 text-[#0062cc] dark:text-blue-400">
            <Spinner size="sm" />
            <p>{statusMessage || 'Processing...'}</p>
          </div>
        )}
        {error && <div className="text-red-600 dark:text-red-400 font-medium">Error: {error}</div>}
        {!isLoading && statusMessage && !error && <div className="text-gray-700 dark:text-gray-300">{statusMessage}</div>}
        {!isLoading && !error && !statusMessage && <div className="text-gray-500 dark:text-gray-400 italic">Ready for next step.</div>}
      </div>

      {/* Dynamic Step UI */}
      <div className="mt-4">
        {renderStepUI()}
      </div>

      {/* Display User Info */}
      {user?.email && <p className="text-xs text-gray-500 dark:text-gray-400 text-center mt-4">Logged in as: {user.email}</p>}
    </div>
  );
}

export default VOCAInteraction;
